import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { ConfirmActionDialog, PillTabs } from '../../../components/design-system/admin';
import { CLEANUP_DAYS, DELETE_ALL_PHRASE } from './queue-monitor.types';
import type { CleanupDays } from './queue-monitor.types';

/** The confirm label, with a spinner while the call runs. */
const Busy = ({ busy, children }: { busy?: boolean; children: ReactNode }) => (
    <>
        {busy && <Loader2 className="animate-spin" />}
        {children}
    </>
);

export function RetryBatchDialog({
    open,
    onOpenChange,
    busy,
    onConfirm,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    busy: boolean;
    onConfirm: () => void | Promise<void>;
}) {
    const { t } = useTranslation();
    return (
        <ConfirmActionDialog
            open={open}
            onOpenChange={onOpenChange}
            title={t('ADMIN_QUEUE.RETRY.TITLE')}
            description={t('ADMIN_QUEUE.RETRY.DESCRIPTION')}
            confirmLabel={<Busy busy={busy}>{t('ADMIN_QUEUE.RETRY.CONFIRM')}</Busy>}
            busy={busy}
            onConfirm={onConfirm}
        />
    );
}

export function CancelBatchDialog({
    open,
    onOpenChange,
    busy,
    onConfirm,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    busy: boolean;
    onConfirm: () => void | Promise<void>;
}) {
    const { t } = useTranslation();
    return (
        <ConfirmActionDialog
            open={open}
            onOpenChange={onOpenChange}
            title={t('ADMIN_QUEUE.CANCEL_BATCH.TITLE')}
            description={t('ADMIN_QUEUE.CANCEL_BATCH.DESCRIPTION')}
            confirmLabel={<Busy busy={busy}>{t('ADMIN_QUEUE.CANCEL_BATCH.CONFIRM')}</Busy>}
            cancelLabel={t('ADMIN_QUEUE.CANCEL_BATCH.KEEP')}
            destructive
            busy={busy}
            onConfirm={onConfirm}
        />
    );
}

/** Completed and failed jobs older than 7, 30 or 90 days, in the current queue. */
export function CleanupJobsDialog({
    open,
    onOpenChange,
    queueName,
    busy,
    onConfirm,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    queueName: string;
    busy: boolean;
    onConfirm: (days: CleanupDays) => void | Promise<void>;
}) {
    const { t } = useTranslation();
    const [days, setDays] = useState<CleanupDays>(30);

    useEffect(() => {
        if (open) setDays(30);
    }, [open]);

    return (
        <ConfirmActionDialog
            open={open}
            onOpenChange={onOpenChange}
            title={t('ADMIN_QUEUE.CLEANUP.TITLE')}
            description={t('ADMIN_QUEUE.CLEANUP.DESCRIPTION', { queue: queueName })}
            confirmLabel={<Busy busy={busy}>{t('ADMIN_QUEUE.CLEANUP.CONFIRM', { count: days })}</Busy>}
            destructive
            busy={busy}
            onConfirm={() => onConfirm(days)}
        >
            <div className="flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">{t('ADMIN_QUEUE.CLEANUP.AGE_LABEL')}</p>
                <PillTabs<CleanupDays>
                    value={days}
                    onChange={setDays}
                    ariaLabel={t('ADMIN_QUEUE.CLEANUP.AGE_LABEL')}
                    options={CLEANUP_DAYS.map((d) => ({ value: d, label: t('ADMIN_QUEUE.CLEANUP.DAYS', { count: d }) }))}
                />
            </div>
        </ConfirmActionDialog>
    );
}

/** Every job of the queue, whatever its status — only once the phrase is typed. */
export function DeleteAllJobsDialog({
    open,
    onOpenChange,
    queueName,
    value,
    onValueChange,
    busy,
    onConfirm,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    queueName: string;
    value: string;
    onValueChange: (value: string) => void;
    busy: boolean;
    onConfirm: () => void | Promise<void>;
}) {
    const { t } = useTranslation();
    return (
        <ConfirmActionDialog
            open={open}
            onOpenChange={onOpenChange}
            title={t('ADMIN_QUEUE.DELETE_ALL.TITLE')}
            description={t('ADMIN_QUEUE.DELETE_ALL.DESCRIPTION', { queue: queueName })}
            confirmLabel={<Busy busy={busy}>{t('ADMIN_QUEUE.DELETE_ALL.CONFIRM')}</Busy>}
            destructive
            busy={busy}
            confirmDisabled={value !== DELETE_ALL_PHRASE}
            onConfirm={onConfirm}
        >
            {/* Irreversible: the action stays locked until the phrase is typed exactly. */}
            <div className="flex flex-col gap-2">
                <Label htmlFor="queue-delete-all-confirm" className="block text-sm leading-normal font-normal text-muted-foreground">
                    {t('ADMIN_QUEUE.DELETE_ALL.TYPE_TO_CONFIRM', { phrase: DELETE_ALL_PHRASE })}
                </Label>
                <Input
                    id="queue-delete-all-confirm"
                    className="font-mono"
                    value={value}
                    onChange={(e) => onValueChange(e.target.value)}
                    placeholder={DELETE_ALL_PHRASE}
                    autoComplete="off"
                    spellCheck={false}
                    disabled={busy}
                />
            </div>
        </ConfirmActionDialog>
    );
}
